"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";

type ModalVariant = "upload" | "delete" | "profile" | "pod" | "style" | "regenerate" | "shop";

const COPY: Record<ModalVariant, { headline: string; body: string }> = {
  upload: {
    headline: "Want to try with your clothes?",
    body: "Sign up to upload your own wardrobe and get personalized outfit suggestions just for you!",
  },
  delete: {
    headline: "Manage your wardrobe",
    body: "Sign up to remove items, organize your wardrobe, and keep your closet up to date.",
  },
  profile: {
    headline: "Make HOKU yours",
    body: "Create an account to save your style preferences and get better recommendations over time.",
  },
  pod: {
    headline: "Create a style pod",
    body: "Sign up to organize your wardrobe into pods — work, weekend, travel, and more.",
  },
  style: {
    headline: "Try your own prompts",
    body: "Sign up to describe your dream outfit and let Hoku bring it to life. These are just a taste!",
  },
  regenerate: {
    headline: "Want more choices?",
    body: "Sign up to regenerate unlimited outfit combinations and discover even more styles!",
  },
  shop: {
    headline: "Love this pick?",
    body: "Sign up to buy products matched to your wardrobe, track prices, and get personalized deals!",
  },
};

interface DemoSignupModalProps {
  open: boolean;
  variant: ModalVariant;
  onClose: () => void;
}

export function DemoSignupModal({ open, variant, onClose }: DemoSignupModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const { headline, body } = COPY[variant];

  const modal = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={headline}
            className="fixed left-1/2 top-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-[#fef9f0] p-6"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <p className="mb-2 font-winky text-[20px] font-bold text-[#1a1a1a]">
              {headline}
            </p>
            <p className="mb-6 font-winky text-[14px] text-[#1a1a1a]/70">{body}</p>
            <Link
              href="/login?action=signup"
              autoFocus
              className="block w-full rounded-lg border-2 border-[#4c4c4c] bg-[#ffd372] py-3 text-center font-winky text-[15px] font-semibold text-[#1a1a1a] shadow-[3px_3px_0px_#4c4c4c] transition-all hover:scale-[1.02] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
            >
              Create Free Account →
            </Link>
            <button
              ref={closeRef}
              onClick={onClose}
              className="mt-3 w-full rounded-lg border border-[#4c4c4c]/20 bg-white py-2 font-winky text-[13px] text-[#1a1a1a]/60 transition-all hover:bg-[#fef9f0]"
            >
              Maybe later
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (!mounted) return null;

  return createPortal(modal, document.body);
}
